import React, { useCallback, useEffect, useState } from 'react';
import Button from '@material-ui/core/Button';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { addHours, format } from 'date-fns';
import utils from './utils';
import 'src/components/css/pagamento.css';

/**
 * Registro de MKR_HUB_PED_VENDA_PGTO_LINK
 * @typedef {Object} LinkPagamento
 * @property {number} id
 * @property {string} nomeResponsavel
 * @property {number} valor
 * @property {string} [url]
 * @property {string} status
 * @property {string} [dataPagamento]
 * @property {string} [dataExpiracao]
 * @property {string} [formaPagamento]
 * @property {number} [quantidadeParcelas]
 * @property {string} [terminalNsu]
 */

const normalizarCodigo = (codigo) =>
  String(codigo || '')
    .trim()
    .toUpperCase();

// Forma de pagamento vem da GetNet (ex.: CREDIT); código sem tradução aparece como veio
const FORMAS_PAGAMENTO = {
  CREDIT: ['credito', 'Crédito'],
  DEBIT: ['debito', 'Débito'],
  PIX: ['pix', 'Pix'],
};

const descreverFormaPagamento = (forma) => {
  const f = FORMAS_PAGAMENTO[normalizarCodigo(forma)];

  return f ? utils.lng(f[0], f[1]) : forma || '';
};

/**
 * Tabela dos links de um pagamento LINK_EXTERNO (copiar, status e regerar).
 * Usada no DialogLinksPagamento (/carrinho/pedido) e na página /links_pagamento/:id (crud HubPedidoVenda);
 * exibirDetalhesPagamento mostra forma de pagamento, parcelas e terminal NSU (só na página).
 */
export default function LinksPagamento(props) {
  const { id_hub_ped_venda_pgto, crud, exibirDetalhesPagamento } = props;

  /** @type {[LinkPagamento[], React.Dispatch<React.SetStateAction<LinkPagamento[]>>]} */
  const [links, setLinks] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [regerandoId, setRegerandoId] = useState(null);

  const carregarLinks = useCallback(() => {
    if (!id_hub_ped_venda_pgto) {
      setLinks([]);
      return;
    }

    setCarregando(true);

    utils.execute(
      crud || {},
      `HubPedidoVendaPagamentoLink?hubPedidoVendaPagamento=${id_hub_ped_venda_pgto}&sortBy=id&sortType=asc&size=-1`,
      null,
      function (res) {
        setLinks(res && res.data ? res.data : []);
        setCarregando(false);
      },
    );
  }, [id_hub_ped_venda_pgto, crud]);

  useEffect(() => {
    carregarLinks();
  }, [carregarLinks]);

  const avisarCopiado = () => {
    window.dialog.alert(utils.lng('copiado_area_transferencia', 'Copiado para área de transferência'));
  };

  const copiarLink = (event, link) => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(link).then(avisarCopiado, (err) => console.error('Erro ao copiar link:', err));
      return;
    }

    const aux = document.createElement('textarea');
    const container = event.currentTarget.parentNode;

    aux.value = link;
    container.appendChild(aux);
    aux.select();

    const copiou = document.execCommand('copy');

    container.removeChild(aux);

    if (copiou) {
      avisarCopiado();
    }
  };

  // O integrar do back ignora link LINK_GERADO (mesmo vencido): marca como ERRO com validade nova (48h, igual
  // à criação) para ele gerar outro na GetNet, trocar a url e voltar para LINK_GERADO. Recarrega a lista no fim.
  const regerarLink = (idLink) => {
    if (regerandoId) return;

    setRegerandoId(idLink);

    utils.call({
      crud: crud || {},
      element: {},
      changed: { status: 'ERRO', dataExpiracao: format(addHours(new Date(), 48), 'dd/MM/yyyy HH:mm:ss') },
      cfg: { url: 'HubPedidoVendaPagamentoLink/' + idLink, method: 'PUT', alert: false },
      complete: function (res) {
        if (!res || res.error) {
          setRegerandoId(null);
          return;
        }

        utils.execute(crud || {}, 'pedido/pagamento/link/integrar?id_link=' + idLink, null, function () {
          setRegerandoId(null);
          carregarLinks();
        });
      },
    });
  };

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{utils.lng('nome_responsavel', 'Nome Responsável')}</TableCell>
          <TableCell align="right">{utils.lng('valor', 'Valor')}</TableCell>
          {exibirDetalhesPagamento && (
            <>
              <TableCell>{utils.lng('forma_pagamento', 'Forma de pagamento')}</TableCell>
              <TableCell align="center">{utils.lng('parcelas', 'Parcelas')}</TableCell>
              <TableCell align="center">{utils.lng('terminal_nsu', 'Terminal NSU')}</TableCell>
            </>
          )}
          <TableCell align="center">{utils.lng('link', 'Link')}</TableCell>
          <TableCell align="center">{utils.lng('status', 'Status')}</TableCell>
          <TableCell align="center"></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {(carregando || links.length === 0) && (
          <TableRow>
            <TableCell colSpan={exibirDetalhesPagamento ? 8 : 5} align="center">
              {carregando
                ? utils.lng('carregando', 'Carregando...')
                : utils.lng('nenhum_link_gerado', 'Nenhum link gerado para este pedido.')}
            </TableCell>
          </TableRow>
        )}
        {!carregando &&
          links.map((l) => {
            const status = normalizarCodigo(l.status);
            const pago = status === 'PAGO' || Boolean(l.dataPagamento);
            const dataExpiracao = l.dataExpiracao ? utils.toDate(l.dataExpiracao) : null;
            const erro =
              status === 'ERRO' ||
              status === 'EXPIRADO' ||
              (utils.isValidDate(dataExpiracao) && dataExpiracao.getTime() < Date.now());

            return (
              <TableRow key={'link-pagamento-' + l.id}>
                <TableCell>{l.nomeResponsavel}</TableCell>
                <TableCell align="right">{utils.money(l.valor)}</TableCell>
                {exibirDetalhesPagamento && (
                  <>
                    <TableCell>{descreverFormaPagamento(l.formaPagamento)}</TableCell>
                    <TableCell align="center">{l.quantidadeParcelas}</TableCell>
                    <TableCell align="center">{l.terminalNsu}</TableCell>
                  </>
                )}
                <TableCell align="center">
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<i className="far fa-copy"></i>}
                    disabled={!l.url || pago || erro}
                    onClick={(e) => copiarLink(e, l.url)}
                  >
                    {utils.lng('copiar_link', 'Copiar link')}
                  </Button>
                </TableCell>
                <TableCell align="center">
                  <span
                    className={
                      'link-status ' +
                      (pago
                        ? 'link-status-pago'
                        : erro
                          ? 'link-status-erro'
                          : l.url
                            ? 'link-status-pendente'
                            : 'link-status-nao-gerado')
                    }
                  >
                    {pago
                      ? utils.lng('pago', 'Pago')
                      : erro
                        ? utils.lng('erro', 'Erro')
                        : l.url
                          ? utils.lng('pendente', 'Pendente')
                          : utils.lng('nao_gerado', 'Não gerado')}
                  </span>
                </TableCell>
                <TableCell align="center">
                  {/* Link já pago não tem o que regerar */}
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    disabled={pago || !erro}
                    startIcon={<i className={'fa fa-sync-alt' + (regerandoId === l.id ? ' fa-spin' : '')}></i>}
                    onClick={() => regerarLink(l.id)}
                  >
                    {utils.lng('regerar_link', 'Regerar link')}
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
      </TableBody>
    </Table>
  );
}
